import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Loader2, ShieldCheck } from "lucide-react";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";

const PERKS = [
  "Every course across all three tracks",
  "Flashcards, quizzes & progress tracking",
  "Black's Law-powered dictionary",
  "Unlimited case-law search + AI answers",
  "AI legal assistant & document drafting",
  "Personal research folder & exports",
];

export default function Subscribe() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState("yearly");

  const subscribe = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, plan });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <Logo className="w-12 h-12 mx-auto mb-6" />
          <span className="text-xs uppercase tracking-widest font-mono text-primary">Membership Required</span>
          <h1 className="font-serif text-5xl mt-3 mb-3">Unlock the full academy</h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}. Membership unlocks every feature of Redemption
            Academy.
          </p>
        </div>

        <div className="border border-border bg-card grain">
          <div className="grid grid-cols-2 border-b border-border">
            <button onClick={() => setPlan("monthly")} data-testid="plan-monthly"
              className={`p-6 text-center transition-colors ${plan === "monthly" ? "bg-primary/10" : "hover:bg-muted"}`}>
              <div className={`font-serif text-3xl ${plan === "monthly" ? "text-primary" : ""}`}>$4.44<span className="text-base text-muted-foreground">/mo</span></div>
              <div className="text-xs font-mono text-muted-foreground mt-1">Monthly</div>
            </button>
            <button onClick={() => setPlan("yearly")} data-testid="plan-yearly"
              className={`p-6 text-center transition-colors relative ${plan === "yearly" ? "bg-primary/10" : "hover:bg-muted"}`}>
              <span className="absolute top-2 right-2 text-[9px] uppercase tracking-widest font-mono bg-primary text-primary-foreground px-2 py-0.5">1 month free</span>
              <div className={`font-serif text-3xl ${plan === "yearly" ? "text-primary" : ""}`}>$44.44<span className="text-base text-muted-foreground">/yr</span></div>
              <div className="text-xs font-mono text-muted-foreground mt-1">Yearly · save 17%</div>
            </button>
          </div>
          <div className="p-10">
            <ul className="grid sm:grid-cols-2 gap-4 mb-10">
              {PERKS.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm">
                  <Check className="w-5 h-5 text-primary shrink-0" /> <span>{p}</span>
                </li>
              ))}
            </ul>
            <button onClick={subscribe} disabled={loading} data-testid="subscribe-btn"
              className="w-full bg-primary text-primary-foreground py-4 rounded-sm text-sm flex items-center justify-center gap-2 hover:-translate-y-[1px] transition-transform disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              {plan === "yearly" ? "Subscribe — $44.44/year" : "Subscribe — $4.44/month"}
            </button>
            <p className="text-xs text-muted-foreground text-center mt-4 font-mono">
              Secure checkout by Stripe · Test card 4242 4242 4242 4242
            </p>
            <button onClick={() => { logout(); navigate("/"); }} className="w-full text-center text-xs text-muted-foreground hover:text-primary mt-6">
              Sign out
            </button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground font-mono text-center mt-8">
          For educational purposes only. None of this shall be construed as legal advice.
        </p>
      </div>
    </div>
  );
}
